<script>
	import { onMount } from "svelte";
	import { fade, blur, fly, slide, scale } from "svelte/transition";
	import RangeSlider from "svelte-range-slider-pips";

	import chroma from "chroma-js";

	import {
		chartOrientation,
		selectedParameters,
		gasStopFirst,
		gasRestart,
		gasStopLast,
		noGasMonths,
		months,
		gasStopSliderValues,
		selectedMonths,
		results,
		animationParameters,
		selectedSliderValues,
		resultsSection,
		parameters,
		parametersById,
		currentLowestPoint,
		currentLowestPointMonth,
		gasStopIndicator,
	} from "./stores.js";

	import { convertBnCubicmetersToTWh, convertTWhtoBnCubicMeters } from "./utils.js";

	import { platform, breakpoint } from "./MediaQuery.svelte";

	import LineChart from "./elements/LineChart.svelte";
	import InOutChart from "./elements/InOutChart.svelte";
	import Parameters from "./elements/Parameters.svelte";
	import Summary from "./elements/Summary.svelte";
	import Headline from "./components/Headline.svelte";
	import ResultIndicator from "./components/ResultIndicator.svelte";

	import dataForPrerendering from "./data/init_data.json";
	import maxValues from "./data/max.json";
	import minValues from "./data/min.json";
	import range from "./data/range.json";
	import verfuegbarkeit2021 from "./data/verfuegbarkeit_2021_2022.json";

	// ///////////////////////////////////////////////////////////
	// Constants
	// ///////////////////////////////////////////////////////////

	const extraFactorDemand = 1.11;
	const barSize = 17;
	const valueKey = "value";
	const gasStorageMax = convertBnCubicmetersToTWh(24.5); // source: https://www.enbw.com/energie-entdecken/verteilung-und-transport/gasnetz/erdgasspeicher.html#:~:text=Deutschland%20verf%C3%BCgt%20%C3%BCber%20die%20gr%C3%B6%C3%9Ften,230%20Milliarden%20Kilowattstunden%20(Arbeitsgasvolumen).

	// const minStorage = 59.5074;
	const minStorage = 0.2419 * gasStorageMax; // in TWh
	const averageIncoming = 605.3;
	const averageOutgoing = 728.7;

	// ///////////////////////////////////////////////////////////
	// Variables binded to UI
	// ///////////////////////////////////////////////////////////

	let selectedScenario = "init";
	let rangeFiltered = [];

	let timeout;
	const setTimeoutGasStopIndicator = () => {
		if ($gasStopIndicator && loadData) {
			clearTimeout(timeout);
		}
		$gasStopIndicator = true;
		timeout = setTimeout(() => {
			$gasStopIndicator = false;
		}, 2000);
	};

	const data = {};
	let loadData = false;

	const getRelevantData = (months, data) => {
		const monthsDates = months.map((m) => +m.date);
		return data.filter((d) => monthsDates.includes(+d.date));
	};

	const addDate = (d) => {
		d.date = new Date(d.year, d.month - 1);
	};

	const processData = (res) => {
		// add real date to the data, convert data to TWh and sort by date
		res.residual.forEach((d) => {
			d.value = convertBnCubicmetersToTWh(d.value);
			addDate(d);
		});
		res.residual.sort((a, b) => b.date - a.date);

		res.stock.forEach((d) => {
			d.value = convertBnCubicmetersToTWh(d.value);
			addDate(d);
		});
		res.stock.sort((a, b) => b.date - a.date);

		// add real date and the bar size to the data and convert data to TWh
		res.supply.forEach((d) => {
			addDate(d);
			d.size = barSize;
			Object.entries(d.data).forEach(([k, v]) => {
				d.data[k] = convertBnCubicmetersToTWh(v);
			});
		});

		res.demand.forEach((d) => {
			addDate(d);
			d.size = barSize;
			Object.entries(d.data).forEach(([k, v]) => {
				d.data[k] = convertBnCubicmetersToTWh(v) * extraFactorDemand;
			});
		});

		// filter data based on the months specified in stores.js
		Object.entries(res).forEach(([key, resData]) => {
			res[key] = getRelevantData($months, resData);
		});

		return res;
	};

	verfuegbarkeit2021.forEach((d) => {
		d.year = d.year + 1;
		d.date = new Date(d.year, d.month - 1);
		d.value = convertBnCubicmetersToTWh(d.value / 10);
	});

	const initData = () => {
		// range data: add date, convert data to TWh and filter relvant data
		range.forEach((d) => {
			d.min = convertBnCubicmetersToTWh(d.min);
			d.max = convertBnCubicmetersToTWh(d.max);
			addDate(d);
		});
		rangeFiltered = getRelevantData($months, range);

		// min values: add date and convert to TWh
		Object.entries(minValues).forEach(([k, v]) => {
			minValues[k] = convertBnCubicmetersToTWh(v);
		});

		// max values: add date and convert to TWh
		Object.entries(maxValues).forEach(([k, v]) => {
			maxValues[k] = convertBnCubicmetersToTWh(v);
		});

		data.init = processData(dataForPrerendering);
	};

	// initialize scenario data, range data and min and max data
	initData();

	let result;
	$: {
		const minVerfuegbarkeit2021 = Math.min(...verfuegbarkeit2021.map((v) => v.value));
		const minScenario = Math.min(...data[selectedScenario].residual.map((v) => v.value));
		if (minScenario < 0) {
			result = "verybad";
		} else if (minScenario < minVerfuegbarkeit2021) {
			result = "neutral";
		} else {
			result = "verygood";
		}
	}

	// ///////////////////////////////////////////////////////////
	// fetch and process data
	// ///////////////////////////////////////////////////////////

	// create current data path
	let dataPath;
	$: {
		const parameterPath = $selectedParameters.reduce(
			(prev, curr) => prev + (prev !== "" ? "_" : "") + curr,
			""
		);

		const scenarioHaushalte = $selectedParameters
			.filter((x) => x.indexOf("haushalte") !== -1)[0]
			.replace("haushalte", "");
		dataPath = `startmonat${$gasStopFirst.month}_${$gasStopFirst.year}_${parameterPath}_fernwaerme53${scenarioHaushalte}`;
	}

	const getData = (scenario) => {
		if (data[scenario]) return Promise.resolve([data[scenario], scenario]);
		return fetch(`${PATH}/data/${scenario}.json`)
			.then((r) => (r.ok ? r.json() : data["init"]))
			.then((r) => {
				data[scenario] = processData(r);
				return [data[scenario], scenario];
			});
	};

	// activate data loading after prerendering
	onMount(async () => {
		loadData = true;
		setTimeoutGasStopIndicator();
	});

	let lastDataPath = "";
	let lastGasStopFirst = $gasStopFirst;

	$: if (loadData && dataPath && dataPath !== lastDataPath)
		getData(dataPath).then(([res, scen]) => {
			if (scen !== dataPath) return; //prevent race
			const scenario = dataPath;
			selectedScenario = scenario;
			lastDataPath = dataPath;
			if (+lastGasStopFirst.date !== +$gasStopFirst.date) setTimeoutGasStopIndicator();
			lastGasStopFirst = $gasStopFirst;
		});

	// ///////////////////////////////////////////////////////////
	// data processing
	// ///////////////////////////////////////////////////////////

	const sumDataByMonths = (months, data) => {
		const relevantData = getRelevantData(months, data);

		const dataKeys = Object.keys(relevantData[0].data);
		const summedData = {
			data: {},
			size: relevantData[0].size,
		};

		dataKeys.forEach((k) => {
			summedData.data[k] = relevantData.reduce((total, obj) => total + obj.data[k], 0);
		});
		return summedData;
	};

	const averageDataByMonths = (months, data) => {
		const relevantData = getRelevantData(months, data);
		const average =
			relevantData.reduce((total, curr) => total + curr.value, 0) / relevantData.length;

		return average;
	};

	const getDataByDate = (date, data) => data.filter((d) => +d.date === +date)[0];

	$: {
		if (selectedScenario) {
			const min = data[selectedScenario].residual
				.filter((d) => +new Date(d.year, d.month - 1) >= +new Date(2022, 9))
				.reduce((prev, curr) => Math.min(prev, curr.value), 100000);
			$currentLowestPoint = data[selectedScenario].residual
				.filter((d) => +new Date(d.year, d.month - 1) >= +new Date(2022, 9))
				.filter((d) => d.value === min)[0];
		}
	}
</script>

<div class="gasrechner">
	<!-- UI -->
	<div class="ui-container" transition:fade>
		<Parameters />
	</div>
	<div class="results-container">
		<!-- <div class="headline" style:color={s.color}>
			<img src={`${PATH}${s.icon}`} class="icon" />
			{s.label}
		</div> -->
		<div class="results-top box">
			<div class="box-inner">
				<div class="headline">
					<Headline
						text={$resultsSection.label}
						icon={$resultsSection.icon}
						color={$resultsSection.color}
					/>
					<ResultIndicator active={result} />
				</div>
				<Summary {result} />
				<!-- CHART LINE-->
				<div class="chart-container-linechart" transition:fade>
					<LineChart
						data={data[selectedScenario].residual}
						colors={[$results[result].color]}
						yTicks={[0, 100, 200, 300]}
						min={minValues.residual}
						max={maxValues.residual}
						range={rangeFiltered}
						{result}
						title="Gasverfügbarkeit laut ausgewähltem Szenario"
						titleIcon={`images/chevron-right-${$results[result].id}.svg`}
						subtitle="im Vergleich zu allen Szenarien und Vorjahresdurchschnitt"
						subtitleIcon="images/chevron-right.svg"
						dataCompare={verfuegbarkeit2021}
					/>
				</div>

				<!-- <div class="box-arrow" /> -->
				<img src={`${PATH}images/box_arrow.svg`} class="box-arrow-image" />
			</div>
		</div>
		<div class="results-bottom box">
			<div class="box-inner">
				<!-- CHART IN OUT-->
				<div class={`chart-container-inoutchart ${$chartOrientation}`} transition:fade>
					<InOutChart
						dataIncoming={sumDataByMonths($selectedMonths, data[selectedScenario].supply)}
						dataOutgoing={sumDataByMonths($selectedMonths, data[selectedScenario].demand)}
						{valueKey}
						barWidth={barSize}
						gasStorageFilled={getDataByDate(
							$currentLowestPointMonth.date,
							data[selectedScenario].stock
						).value}
						gasStorageMonth={$currentLowestPointMonth.name}
						{gasStorageMax}
						maxValue={Math.max(maxValues.supply, maxValues.demand)}
						compareValueIncoming={averageIncoming}
						compareValueOutgoing={averageOutgoing}
						compareValueStorage={minStorage}
						compareLabel={"2021"}
						gasStorageValueColor={"#2a2a2a"}
					/>
				</div>
			</div>
		</div>
	</div>
</div>
<div class="gr-attribution-container">
	<div class="gr-attribution-left" />
	<div class="gr-attribution">
		<span>Daten:</span>
		<a
			href="https://www.ifw-kiel.de/fileadmin/Dateiverwaltung/Media/Images/News_Press_Releases/2022/gemeinschaftsdiagnose_sonderauswertung-gasluecke-juni-2022_V2.pdf"
			target="_blank">Projektgruppe Gemeinschaftsdiagnose</a
		>,
		<a
			href="https://www.diw.de/de/diw_01.c.838843.de/publikationen/diw_aktuell/2022_0083/energieversorgung_in_deutschland_auch_ohne_erdgas_aus_russland_gesichert.html"
			target="_blank">DIW</a
		>,
		<a
			href="https://www.bdew.de/media/documents/Kuzfristige_Gassubstitution_Deutschland__final_17.03.2022_korr1.pdf"
			target="_blank">BDEW</a
		>,
		<a
			href="https://ag-energiebilanzen.de/wp-content/uploads/2020/10/ageb_20v_v1.pdf"
			target="_blank">AG-Energie&shy;bilanzen</a
		>,
		<a
			href="https://www.heizspiegel.de/fileadmin/hs/heizspiegel/heizspiegel-2021/heizspiegel-2021.pdf"
			target="_blank">Heizspiegel</a
		>,
		<a
			href="https://dejure.org/dienste/vernetzung/rechtsprechung?Gericht=LG%20Berlin&Datum=26.05.1998&Aktenzeichen=64%20S%20266/97"
			target="_blank">Landgericht Berlin</a
		>.
	</div>
</div>

<style lang="scss">
	.gasrechner {
		font-family: "Franklin Gothic FS", Helvetica, Arial, sans-serif;
		font-size: 14px;
		display: flex;
		// flex-wrap: wrap;
		// max-width: 700px;
		margin: 0px auto;
		color: #2a2a2a;
		position: relative;
		z-index: 1;
		flex-direction: column;
	}

	.results-container {
		margin-top: 0;
		padding-bottom: 20px;
		// width: 100%;

		.headline {
			display: flex;
			justify-content: space-between;
		}

		.results-top {
			z-index: 1;

			// .box-arrow {
			// 	z-index: unset;
			// }

			// .box-arrow-image {
			// 	z-index: unset;
			// }
		}

		.results-bottom {
			margin-bottom: -28px;
			background: linear-gradient(
				180.15deg,
				rgba(228, 228, 228, 0.2) 21.68%,
				rgba(228, 228, 228, 0) 63%
			);

			.box-inner {
				padding-top: 33px;
				margin-top: -25px;
			}
		}
	}

	.debug {
		width: 100%;

		> div {
			margin: 10px auto;
		}
	}

	.chart-container-inoutchart {
		display: flex;
		flex-basis: 50%;
		flex-grow: 100;
		align-items: center;
	}

	.chart-container-linechart {
		width: 100%;
		// height: 187px;
	}

	:global(.box) {
		position: relative;
		// filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.25));
		box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.25);
		border-radius: 10px;
	}

	:global(.box-inner) {
		background: #ffffff;
		border-radius: 10px;
		padding: 12px 15px 15px 15px;
		margin-bottom: 8px;
		position: relative;
		height: 100%;
		box-sizing: border-box;
	}

	// :global(.box-arrow) {
	// 	position: absolute;
	// 	left: 50%;
	// 	top: 100%;
	// 	background: #fff;
	// 	width: 18px;
	// 	height: 18px;
	// 	border-radius: 3px;
	// 	z-index: -1;
	// 	transform-origin: center center;
	// 	transform: translate(-50%, -50%) rotate(45deg);
	// }

	:global(.box-arrow-image) {
		position: absolute;
		left: 50%;
		top: 100%;
		width: 33px;
		height: 33px;
		z-index: -1;
		transform-origin: center center;
		transform: translate(-50%, -50%);
	}

	// Desktop layout
	@media (min-width: 840px) {
		.gasrechner {
			// column-gap: 30px;
			// row-gap: 30px;
			flex-direction: row;
			align-items: center;
		}

		.gr-attribution-container {
			display: flex;
			flex-direction: row;
		}

		.ui-container,
		.gr-attribution-left {
			flex-basis: 43% !important;
			min-width: 423px !important;
			z-index: -1;
		}

		.results-container,
		.gr-attribution {
			flex-basis: 50%;
			flex-grow: 1;
		}

		.results-container {
			.headline {
				font-size: 20px !important;
			}
		}
	}

	// Rechner auf Textbreite beschränken
	@media (max-width: 709px) {
		.gasrechner {
			max-width: 620px;
		}

		.gr-attribution-container {
			display: flex;
			justify-content: center;
		}
	}

	// Tablet view
	@media (min-width: 710px) and (max-width: 839px) {
		.gasrechner {
			align-items: center;
			row-gap: 8px;
		}

		.ui-container {
			display: flex;
			flex-direction: row;
			column-gap: 10px;
			width: 100%;
		}

		.results-container {
			max-width: 620px;
		}

		.gr-attribution-container {
			display: flex;
			justify-content: center;
		}
	}

	// mehr Padding
	@media (min-width: 710px) {
		.results-container {
			.results-top {
				.box-inner {
					padding: 18px 24px 24px 24px;
				}
			}

			.results-bottom {
				.box-inner {
					padding: 33px 24px 24px 24px;
				}
			}
		}

		.gr-attribution {
			font-size: 11.5px !important;
			line-height: 12px !important;
		}
	}

	// @media (min-width: 680px) and (max-width: 839px) {
	// 	.ui-container,
	// 	.gr-attribution-left {
	// 		flex-basis: 43% !important;
	// 		min-width: 384px !important;
	// 		z-index: -1;
	// 	}

	// 	.results-container,
	// 	.gr-attribution {
	// 		flex-basis: 50%;
	// 		flex-grow: 1;
	// 	}

	// 	.results-container {
	// 		border-radius: 10px;
	// 		box-shadow: 0px 0px 6px rgba(0, 0, 0, 0.12);
	// 	}
	// }

	.gr-attribution {
		font-family: "Franklin Gothic FS", Helvetica, Arial, sans-serif;
		color: #b5b5b5;
		margin-top: 15px;
		font-size: 10px;
		line-height: 11px;
		letter-spacing: 0.015em;

		a {
			font-weight: 500;
			color: #b5b5b5;
			text-decoration: none;
			background-image: none;
			text-shadow: none;
		}
	}
</style>
